import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { PostItem } from '../feed/PostItem';

const PostFeed = ({
  resetKey = 'default',
  posts = [],
  handles = {},
  CreatePostEl = null,
  hasMore = false,
  loadMoreItems = () => {},
  isLoading = true,
  can_post = true
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const parentOffsetRef = useRef(0);
  const heightCache = useRef(new Map());
  const observerMap = useRef(new Map());
  const elementRefs = useRef(new Map());
  const lastLoadTriggerRef = useRef(null);
  const previousPostsLengthRef = useRef(posts.length);
  const activeResetKeyRef = useRef(resetKey);

  const location = useLocation();
  const NAV_HEIGHT = 0;

  useEffect(() => {
    const offset = document.querySelector('.app-layout')?.offsetTop || 0;
    parentOffsetRef.current = offset + NAV_HEIGHT;
  }, []);

  const hasCreatePostRow = Boolean(CreatePostEl);

  const getItemKey = (index) => {
    if (hasCreatePostRow && index === 0) return 'create-post';

    const postIndex = hasCreatePostRow ? index - 1 : index;
    const post = posts[postIndex];
    return post?.feed?.action_id ? `post-${post?.feed?.action_id}` : `loading-${index}`;
  };

  const rowVirtualizer = useWindowVirtualizer({
    count: posts.length + (hasCreatePostRow ? 1 : 0),
    getItemKey: getItemKey,
    estimateSize: (index) => {
      const key = getItemKey(index); 
      return heightCache.current.get(key) || 600; //@TODO: Adjust specifically for if can_post = false
    },
    overscan: 5,
    scrollMargin: parentOffsetRef.current,
    useAnimationFrameWithResizeObserver: true
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    if (posts.length < previousPostsLengthRef.current) {
      lastLoadTriggerRef.current = null;
    }
    previousPostsLengthRef.current = posts.length;
  }, [posts.length]);

  useEffect(() => {
    if (!virtualItems.length) return;
    if (!hasMore || isLoading) return;

    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;

    const lastDataIndex = posts.length - 1;
    const lastVirtualDataIndex = hasCreatePostRow ? lastItem.index - 1 : lastItem.index;

    if (lastVirtualDataIndex < lastDataIndex) return;

    const triggerKey = `${resetKey}:${posts.length}`;
    if (lastLoadTriggerRef.current === triggerKey) return;

    lastLoadTriggerRef.current = triggerKey;
    loadMoreItems();
  }, [virtualItems, hasMore, isLoading, posts.length, hasCreatePostRow, loadMoreItems, resetKey]);


  useEffect(() => {
    const getScrollTop = () => {
      const el = rowVirtualizer.scrollElement ?? window;
      return el?.scrollTop ?? window.scrollY;
    };

    const saveScroll = () => {
      const scrollTop = getScrollTop();
      if (scrollTop > 0) {
        sessionStorage.setItem('postFeedScrollTop', scrollTop);
      }
    };
    saveScroll();
  }, [location.pathname]);

  const hasRestoredScroll = useRef(false);
  useEffect(() => {
    if (hasRestoredScroll.current) return;
    if (!posts.length) return;

    const scrollY = sessionStorage.getItem('postFeedScrollTop');
    if (!scrollY) return;

    const scrollEl = rowVirtualizer.scrollElement ?? window;
    requestAnimationFrame(() => {
      console.log('Restoring scroll position to:', scrollY);
      scrollEl.scrollTo(0, parseInt(scrollY, 10));
      hasRestoredScroll.current = true;
    });
  }, [posts.length]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollEl = rowVirtualizer.scrollElement ?? window;
      const scrollTop = scrollEl.scrollTop || window.scrollY;
      setIsScrolled(scrollTop > 200);
      // console.log("[DEBUG] Scroll Top:", scrollTop);
    };

    const scrollEl = rowVirtualizer.scrollElement ?? window;
    scrollEl.addEventListener('scroll', handleScroll);
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [rowVirtualizer.scrollElement]);

  useEffect(() => {
    return () => {
      for (const observer of observerMap.current.values()) {
        observer.disconnect();
      }
      observerMap.current.clear();
    };
  }, []);

  useEffect(() => {
    //console.log("[DEBUG] Height Cache Snapshot:");
    for (let [key, value] of heightCache.current.entries()) {
      //console.log(`  ${key}: ${value}px`);
    }
  }, [posts]);


  const observeResize = (el, key) => {
    if (observerMap.current.has(key)) {
      observerMap.current.get(key).disconnect();
      observerMap.current.delete(key);
    }
    const resizeObserver = new ResizeObserver(entries => {
    requestAnimationFrame(() => {
      for (let entry of entries) {
        const newHeight = Math.round(entry.contentRect.height);
        const prevHeight = heightCache.current.get(key);
        
        if ((newHeight !== prevHeight) && (newHeight > 0)) {
          //console.log("New height for ", key, ": ", newHeight, " Previous height: ", prevHeight);
          heightCache.current.set(key, newHeight);
          rowVirtualizer.measure();
        }
      }
    });
  });

    resizeObserver.observe(el);
    observerMap.current.set(key, resizeObserver);
  };

  useEffect(() => {
    if (activeResetKeyRef.current === resetKey) {
      return;
    }

    activeResetKeyRef.current = resetKey;
    lastLoadTriggerRef.current = null;
    previousPostsLengthRef.current = posts.length;

    for (const observer of observerMap.current.values()) {
      observer.disconnect();
    }
    observerMap.current.clear();
    heightCache.current.clear();
    elementRefs.current.clear();
    hasRestoredScroll.current = false;
    sessionStorage.removeItem('postFeedScrollTop');

    const scrollEl = rowVirtualizer.scrollElement ?? window;
    if (scrollEl && typeof scrollEl.scrollTo === 'function') {
      scrollEl.scrollTo(0, 0);
    } else if (typeof window.scrollTo === 'function') {
      window.scrollTo(0, 0);
    }

    try {
      rowVirtualizer.scrollToIndex(0, { align: 'start' });
    } catch (error) {
      console.warn('Failed to reset virtualizer index', error);
    }

    rowVirtualizer.measure();
  }, [resetKey, rowVirtualizer, posts.length]);

  return (
    <div style={{ position: 'relative', height: rowVirtualizer.getTotalSize() }}>
      {virtualItems.map((virtualRow) => {
        const index = virtualRow.index;
        const isCreatePostRow = hasCreatePostRow && index === 0;
        const postIndex = hasCreatePostRow ? index - 1 : index;
        const post = !isCreatePostRow ? posts[postIndex] : null;

        let content;
        if (isCreatePostRow && CreatePostEl) {
          content = typeof CreatePostEl === 'function' ? CreatePostEl() : CreatePostEl;
        } else {
          content = post && post.feed ? <PostItem postData={post} handles={handles} /> : null;
        }

        const key = getItemKey(index);

        return (
          <div
            key={key}
            data-index={index}
            ref={(el) => {
              if (!el) return;
              const cached = elementRefs.current.get(key);
              if (cached === el) return;
              elementRefs.current.set(key, el);

              const height = Math.round(el.getBoundingClientRect().height);
              if (height !== 0 && heightCache.current.get(key) !== height) {
                // console.log("Setting height for ", key, ": ", height);
                // heightCache.current.set(key, height);
                rowVirtualizer.measure();
              }

              observeResize(el, key);
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
              boxSizing: 'border-box',
              marginBottom: '1rem'
            }}
          >
            {content}
          </div>
        );
      })}

      <div className={`scroll-button-mini ${isScrolled ? 'show' : ''}`}>
        <Button
          className="btn-primary"
          onClick={() => {
            const scrollEl = rowVirtualizer.scrollElement ?? window;
            scrollEl.scrollTo(0, 0);
          }}
        >
          <FontAwesomeIcon
            icon={faArrowUp}
            style={{ color: 'white', fontSize: '1.5em' }}
          />
        </Button>
      </div>
    </div>
  );
};

export default PostFeed;
